/**
 * ImpactSignatureMini — Mini radar for the left nav (Variant H)
 *
 * Medal-colored tier rings (Bronze → Silver → Gold → Platinum),
 * spokes, wire-only shape (no fill), and dimension dots.
 * Clicking navigates to the Impact page's Growth tab.
 *
 * SVG uses a native 120×115 viewBox so coordinates map 1:1 to
 * display pixels — no downscaling, no sub-pixel aliasing.
 *
 * Visibility is controlled by `showMiniRadar` prop — gated by
 * per-user progressive disclosure (user must have "graduated" to it).
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './ImpactSignatureMini.module.css';

/* ── Tier-level type (matches DimensionRadar.tsx) ── */
export type TierLevel = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum';

interface ImpactSignatureMiniProps {
  /** Per-dimension tier levels. Keys: mind, voice, heart, body, wings, roots */
  tiers?: Record<string, TierLevel>;
  /** Whether the nav is collapsed (icon-only mode) */
  collapsed?: boolean;
  /**
   * DEV-1039. When true (default), the radar is clickable and navigates to
   * the user's Growth tab. Set to false in contexts where the radar is a
   * preview only — e.g. inside the Owners & Admins tray, where clicking
   * would dismiss the tray and lose unsaved form state.
   *
   * When false: no navigate, no role/tabIndex, no keyboard activation, no
   * pointer cursor. The radar still renders identically (animations, tiers,
   * colors) — only the interaction layer is suppressed.
   */
  interactive?: boolean;
}

/* ── Geometry: native 120×115 coordinate space ── */
/* Center of hex at (60, 55), max radius 48 to fit in viewBox with padding */
const CX = 60;
const CY = 55;

/** Hex vertices at radius R, starting from top (−90°), clockwise */
function hexPoints(r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 90);
    const x = Math.round(CX + r * Math.cos(angle));
    const y = Math.round(CY + r * Math.sin(angle));
    return `${x},${y}`;
  }).join(' ');
}

/* ── Tier radii (scaled to fit 120×115 viewBox) ── */
/*    Original: bronze=35, silver=70, gold=105, platinum=140 out of 140 max
 *    New max radius: 48px → scale factor: 48/140 ≈ 0.343                  */
const SCALE = 48 / 140;
const TIER_RADIUS: Record<string, number> = {
  bronze:   35 * SCALE,   // ~12
  silver:   70 * SCALE,   // ~24
  gold:    105 * SCALE,   // ~36
  platinum: 140 * SCALE,  // 48
};

/* ── Dimension → vertex index (top=0, clockwise) ── */
const DIM_ORDER = ['mind', 'voice', 'heart', 'body', 'wings', 'roots'] as const;
const DIM_COLORS: Record<string, string> = {
  mind: 'var(--dim-mind)',
  voice: 'var(--dim-voice)',
  heart: 'var(--dim-heart)',
  body: 'var(--dim-body)',
  wings: 'var(--dim-wings)',
  roots: 'var(--dim-roots)',
};

const TIER_TO_RADIUS: Record<TierLevel, number> = {
  none:     0,
  bronze:   35 * SCALE,
  silver:   70 * SCALE,
  gold:    105 * SCALE,
  platinum: 140 * SCALE,
};

/** Default empty tiers — all dimensions at center (no impact yet) */
const EMPTY_TIERS: Record<string, TierLevel> = {
  mind: 'none',
  voice: 'none',
  heart: 'none',
  body: 'none',
  wings: 'none',
  roots: 'none',
};

function tierVertex(dimIndex: number, tier: TierLevel): { x: number; y: number } {
  const r = TIER_TO_RADIUS[tier];
  if (r === 0) return { x: CX, y: CY };
  const angle = (Math.PI / 180) * (60 * dimIndex - 90);
  return {
    x: Math.round(CX + r * Math.cos(angle)),
    y: Math.round(CY + r * Math.sin(angle)),
  };
}

/* ── Floor-tier detection ── */
const TIER_ORDER: TierLevel[] = ['none', 'bronze', 'silver', 'gold', 'platinum'];

function getUniformTier(t: Record<string, TierLevel>): TierLevel | null {
  const indices = DIM_ORDER.map((k) => TIER_ORDER.indexOf(t[k] || 'none'));
  const minIdx = Math.min(...indices);
  if (minIdx <= 0) return null;
  return TIER_ORDER[minIdx];
}

/** Hex vertex at radius R, vertex index i (0=top, clockwise, matching DIM_ORDER) */
function hexVertex(r: number, i: number): [number, number] {
  const angle = (Math.PI / 180) * (60 * i - 90);
  return [
    Math.round(CX + r * Math.cos(angle)),
    Math.round(CY + r * Math.sin(angle)),
  ];
}

/* ── Medal ring colors (CSS vars with fallbacks) ── */
const MEDAL_COLORS = {
  platinum: { stroke: 'var(--medal-platinum, #C8C6C2)', fill: 'var(--medal-platinum, #C8C6C2)' },
  gold:     { stroke: 'var(--medal-gold, #D4A843)',     fill: 'var(--medal-gold, #D4A843)' },
  silver:   { stroke: 'var(--medal-silver, #B0B8C4)',   fill: 'var(--medal-silver, #B0B8C4)' },
  bronze:   { stroke: 'var(--medal-bronze, #CD7F32)',   fill: 'var(--medal-bronze, #CD7F32)' },
};

/* ── Spoke endpoints (to platinum vertices) ── */
const SPOKE_ENDS = Array.from({ length: 6 }, (_, i) => {
  const angle = (Math.PI / 180) * (60 * i - 90);
  return {
    x: Math.round(CX + TIER_RADIUS.platinum * Math.cos(angle)),
    y: Math.round(CY + TIER_RADIUS.platinum * Math.sin(angle)),
  };
});

export default function ImpactSignatureMini({
  tiers = EMPTY_TIERS,
  collapsed = false,
  interactive = true,
}: ImpactSignatureMiniProps) {
  const navigate = useNavigate();
  const { t } = useAppTranslation('leftNav');

  /* Floor-tier: highest tier ALL 6 dimensions have reached */
  const uniformTier = useMemo(() => getUniformTier(tiers), [tiers]);

  /* Floor-shape hex points (regular hex at floor tier radius) */
  const floorShapePoints = uniformTier ? hexPoints(TIER_TO_RADIUS[uniformTier]) : '';

  /* Cube face vertices for isometric shading */
  const cubeFaces = useMemo(() => {
    if (!uniformTier) return null;
    const r = TIER_TO_RADIUS[uniformTier];
    if (r === 0) return null;
    const verts = Array.from({ length: 6 }, (_, i) => hexVertex(r, i));
    return {
      top:   `${verts[5].join(',')} ${verts[0].join(',')} ${verts[1].join(',')} ${CX},${CY}`,
      right: `${verts[1].join(',')} ${verts[2].join(',')} ${verts[3].join(',')} ${CX},${CY}`,
      left:  `${verts[3].join(',')} ${verts[4].join(',')} ${verts[5].join(',')} ${CX},${CY}`,
    };
  }, [uniformTier]);

  /* Build wire-shape points from tier data */
  const wirePoints = DIM_ORDER.map((dim, i) => {
    const v = tierVertex(i, tiers[dim] ?? 'none');
    return `${v.x},${v.y}`;
  }).join(' ');

  /* Dot positions */
  const dots = DIM_ORDER.map((dim, i) => {
    const v = tierVertex(i, tiers[dim] ?? 'none');
    return { dim, x: v.x, y: v.y, color: DIM_COLORS[dim] };
  });

  const handleClick = () => {
    navigate('/?tab=growth'); // Impact page → My Growth tab
  };

  /* DEV-1039. When interactive=false, render as a presentational div with no
     navigation, no role/tabIndex, no keyboard handler, no cursor:pointer.
     The CSS `.static` modifier suppresses hover background and focus outline. */
  const containerProps = interactive
    ? {
        className: `${styles.slot} ${collapsed ? styles.collapsed : ''}`,
        onClick: handleClick,
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        },
        title: t('impactSignature'),
        'aria-label': t('impactSignature'),
      }
    : {
        className: `${styles.slot} ${styles.static} ${collapsed ? styles.collapsed : ''}`,
        'aria-label': t('impactSignature'),
      };

  return (
    <div {...containerProps}>
      <svg
        className={styles.svg}
        width="120"
        height="115"
        viewBox="0 0 120 115"
        aria-hidden="true"
      >
        {/* ── SVG gradient defs for metallic fill ── */}
        <defs>
          <linearGradient id="mini-grad-bronze" x1="0%" y1="0%" x2="100%" y2="30%">
            <stop offset="0%" stopColor="#8B5E34" />
            <stop offset="18%" stopColor="#CD7F32" />
            <stop offset="35%" stopColor="#E8A84C" />
            <stop offset="48%" stopColor="#FFC87C" />
            <stop offset="55%" stopColor="#E8A84C" />
            <stop offset="70%" stopColor="#CD7F32" />
            <stop offset="88%" stopColor="#A0642A" />
            <stop offset="100%" stopColor="#8B5E34" />
          </linearGradient>
          <linearGradient id="mini-grad-silver" x1="0%" y1="0%" x2="100%" y2="30%">
            <stop offset="0%" stopColor="#7A8490" />
            <stop offset="18%" stopColor="#A8B4C0" />
            <stop offset="35%" stopColor="#C8D0D8" />
            <stop offset="48%" stopColor="#F0F4F8" />
            <stop offset="55%" stopColor="#C8D0D8" />
            <stop offset="70%" stopColor="#A8B4C0" />
            <stop offset="88%" stopColor="#8A96A4" />
            <stop offset="100%" stopColor="#7A8490" />
          </linearGradient>
          <linearGradient id="mini-grad-gold" x1="0%" y1="0%" x2="100%" y2="30%">
            <stop offset="0%" stopColor="#B8962E" />
            <stop offset="18%" stopColor="#D4AF37" />
            <stop offset="35%" stopColor="#E8C84A" />
            <stop offset="48%" stopColor="#FFF4B0" />
            <stop offset="55%" stopColor="#FFD700" />
            <stop offset="70%" stopColor="#D4AF37" />
            <stop offset="88%" stopColor="#B8962E" />
            <stop offset="100%" stopColor="#9A7C22" />
          </linearGradient>
          <linearGradient id="mini-grad-platinum" x1="0%" y1="0%" x2="100%" y2="30%">
            <stop offset="0%" stopColor="#2A2D32" />
            <stop offset="15%" stopColor="#3E4248" />
            <stop offset="30%" stopColor="#555B63" />
            <stop offset="44%" stopColor="#7A8290" />
            <stop offset="56%" stopColor="#555B63" />
            <stop offset="72%" stopColor="#3E4248" />
            <stop offset="88%" stopColor="#2A2D32" />
            <stop offset="100%" stopColor="#222528" />
          </linearGradient>
          {/* Soft glow filter for the metallic fill perimeter */}
          <filter id="mini-shape-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── Medal tier rings (outermost first) ── */}
        <polygon
          points={hexPoints(TIER_RADIUS.platinum)}
          fill={MEDAL_COLORS.platinum.fill}
          fillOpacity={0.08}
          stroke={MEDAL_COLORS.platinum.stroke}
          strokeWidth={1.5}
          strokeOpacity={0.65}
          strokeLinejoin="round"
          className={styles.ringPulse}
        />
        <polygon
          points={hexPoints(TIER_RADIUS.gold)}
          fill={MEDAL_COLORS.gold.fill}
          fillOpacity={0.10}
          stroke={MEDAL_COLORS.gold.stroke}
          strokeWidth={1}
          strokeOpacity={0.55}
          strokeLinejoin="round"
        />
        <polygon
          points={hexPoints(TIER_RADIUS.silver)}
          fill={MEDAL_COLORS.silver.fill}
          fillOpacity={0.10}
          stroke={MEDAL_COLORS.silver.stroke}
          strokeWidth={1}
          strokeOpacity={0.50}
          strokeLinejoin="round"
        />
        <polygon
          points={hexPoints(TIER_RADIUS.bronze)}
          fill={MEDAL_COLORS.bronze.fill}
          fillOpacity={0.12}
          stroke={MEDAL_COLORS.bronze.stroke}
          strokeWidth={1}
          strokeOpacity={0.55}
          strokeLinejoin="round"
        />

        {/* ── Spokes ── */}
        {SPOKE_ENDS.map((end, i) => (
          <line
            key={i}
            x1={CX} y1={CY}
            x2={end.x} y2={end.y}
            stroke="var(--text-secondary)"
            strokeWidth={0.5}
            opacity={0.25}
          />
        ))}

        {/* ── Metallic floor-tier fill (when all dimensions reach same tier) ── */}
        {uniformTier && (
          <>
            {/* Perimeter glow */}
            <polygon
              className={styles.fillGlow}
              points={floorShapePoints}
              fill="none"
              stroke={`url(#mini-grad-${uniformTier})`}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeOpacity={0.3}
              filter="url(#mini-shape-glow)"
            />
            {/* Metallic fill hex */}
            <polygon
              className={styles.metallicFill}
              points={floorShapePoints}
              fill={`url(#mini-grad-${uniformTier})`}
              fillOpacity={0.55}
              stroke={`url(#mini-grad-${uniformTier})`}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
            {/* Isometric cube shading */}
            {cubeFaces && (
              <>
                <polygon points={cubeFaces.top} fill="white" fillOpacity={0.08} />
                <polygon points={cubeFaces.right} fill="black" fillOpacity={0.04} />
                <polygon points={cubeFaces.left} fill="black" fillOpacity={0.10} />
              </>
            )}
          </>
        )}

        {/* ── Wire shape (stroke only, no fill) ── */}
        <polygon
          points={wirePoints}
          fill="none"
          stroke="var(--text-primary, #e8eaf0)"
          strokeWidth={1.5}
          strokeLinejoin="round"
          className={styles.wireBreathe}
        />

        {/* ── Dimension dots ── */}
        {dots.map((d, i) => (
          <circle
            key={d.dim}
            cx={d.x}
            cy={d.y}
            r={3}
            fill={d.color}
            className={`${styles.dotBreathe} ${styles[`dotD${i + 1}`]}`}
          />
        ))}
      </svg>

    </div>
  );
}
